import cloneDeep from "lodash/cloneDeep";
import { compareTwoPanelNumbers } from "../../hvSubstations/PanelNumberOrdering";
import {
  AccessLocation,
  MeasurementAccessory,
  MeasurementComponent,
  MeasurementType,
  SubLocation,
} from "../../shared/enums/Result";
import type { GroupedMeasurement, OrderedMeasurement } from "./types";

const measurementOrder = {
  types: [
    MeasurementType.TEV,
    MeasurementType.Ultra,
    MeasurementType.CablePD,
    MeasurementType.UHF,
    MeasurementType.VPIS,
    undefined,
  ],
  accessories: [
    MeasurementAccessory.ULTRA_INT,
    MeasurementAccessory.ULTRA_FLEX,
    MeasurementAccessory.ULTRA_CONT,
    MeasurementAccessory.ULTRA_DISH,
    MeasurementAccessory.ULTRA_BOLT,
    undefined,
  ],
  accesslocs: [AccessLocation.Front, AccessLocation.Back, AccessLocation.Other, undefined],
  components: [
    MeasurementComponent.CircuitBreaker,
    MeasurementComponent.Cables,
    MeasurementComponent.CableBox,
    MeasurementComponent.CTChamber,
    MeasurementComponent.VTChamber,
    MeasurementComponent.Spouts,
    MeasurementComponent.UpperBusbars,
    MeasurementComponent.LowerBusbars,
    MeasurementComponent.PrimaryCables,
    MeasurementComponent.PrimaryCableBox,
    MeasurementComponent.PrimaryBushing,
    MeasurementComponent.MainTank,
    MeasurementComponent.TapChanger,
    MeasurementComponent.SecondaryBushing1,
    MeasurementComponent.SecondaryCables1,
    MeasurementComponent.SecondaryCableBox1,
    MeasurementComponent.SecondaryBushing2,
    MeasurementComponent.SecondaryCableBox2,
    MeasurementComponent.SecondaryCables2,
    MeasurementComponent.OverheadSwitch1,
    MeasurementComponent.OverheadSwitch2,
    MeasurementComponent.VoltageTransformer,
    MeasurementComponent.CurrentTransformer,
    MeasurementComponent.BusbarSection1,
    MeasurementComponent.BusbarSection2,
    MeasurementComponent.LightningArrester,
    MeasurementComponent.IncomerCable,
    MeasurementComponent.CircuitBreakerExpanded,
    MeasurementComponent.RearLower,
    MeasurementComponent.RearMiddle,
    MeasurementComponent.RearUpper,
    MeasurementComponent.FrontLower,
    MeasurementComponent.FrontMiddle,
    MeasurementComponent.FrontUpper,
    MeasurementComponent.DisconnectorSwitch1,
    MeasurementComponent.DisconnectorSwitch2,
    MeasurementComponent.GasInsulatedLine,
    MeasurementComponent.EarthingSwitch,
    MeasurementComponent.CurrentTransformer1,
    MeasurementComponent.CurrentTransformer2,
    MeasurementComponent.Busbar1,
    MeasurementComponent.Busbar2,
    undefined,
  ],
  sublocs: [
    SubLocation.None,
    SubLocation.P1_C1,
    SubLocation.P2_C1,
    SubLocation.P3_C1,
    SubLocation.P1_C2,
    SubLocation.P2_C2,
    SubLocation.P3_C2,
    SubLocation.P1_C3,
    SubLocation.P2_C3,
    SubLocation.P3_C3,
    SubLocation.P1_C4,
    SubLocation.P2_C4,
    SubLocation.P3_C4,
    SubLocation.P1_C5,
    SubLocation.P2_C5,
    SubLocation.P3_C5,
    SubLocation.P1_C6,
    SubLocation.P2_C6,
    SubLocation.P3_C6,
    SubLocation.SUB_LOC_UPPER_LEFT,
    SubLocation.SUB_LOC_UPPER_CENTRE,
    SubLocation.SUB_LOC_UPPER_RIGHT,
    SubLocation.SUB_LOC_CENTRE_LEFT,
    SubLocation.SUB_LOC_CENTRE_RIGHT,
    SubLocation.SUB_LOC_LEFT,
    SubLocation.SUB_LOC_CENTRE,
    SubLocation.SUB_LOC_RIGHT,
    SubLocation.SUB_LOC_LOWER_LEFT,
    SubLocation.SUB_LOC_LOWER_CENTRE,
    SubLocation.SUB_LOC_LOWER_RIGHT,
    SubLocation.SUB_LOC_TANK_L1,
    SubLocation.SUB_LOC_TANK_L2,
    SubLocation.SUB_LOC_TANK_L3,
    SubLocation.SUB_LOC_BUSHING_L1,
    SubLocation.SUB_LOC_BUSHING_L2,
    SubLocation.SUB_LOC_BUSHING_L3,
    SubLocation.SUB_LOC_STRUCTURE_L1,
    SubLocation.SUB_LOC_STRUCTURE_L2,
    SubLocation.SUB_LOC_STRUCTURE_L3,
    SubLocation.SUB_LOC_CONDUCTOR_CONNECTION_L1,
    SubLocation.SUB_LOC_CONDUCTOR_CONNECTION_L2,
    SubLocation.SUB_LOC_CONDUCTOR_CONNECTION_L3,
    undefined,
  ],
};

const measurementTypeOrder = createOrderMap(measurementOrder.types);
const measurementAccessoryOrder = createOrderMap(measurementOrder.accessories);
const accessLocationOrder = createOrderMap(measurementOrder.accesslocs);
const measurementComponentOrder = createOrderMap(measurementOrder.components);
const subLocationOrder = createOrderMap(measurementOrder.sublocs);

export function clearAllMeasurementPanels(groupedMeasurements: GroupedMeasurement[]): GroupedMeasurement[] {
  return groupedMeasurements.map((measurement) => ({
    ...measurement,
    panels: [],
  }));
}

export function rebuildMeasurementsOrder(
  groupedMeasurements: GroupedMeasurement[],
): [GroupedMeasurement[], OrderedMeasurement[]] {
  const groupedMeasurementsClone = cloneDeep(groupedMeasurements);
  const orderedMeasurements = groupedMeasurementsToOrderedMeasurements(groupedMeasurementsClone);
  const orderedMeasurementsDefaultOrder = setOrderedMeasurementsDefaultOrder(orderedMeasurements);
  const groupedMeasurementsOrdered = setGroupedMeasurementsOrder(
    orderedMeasurementsDefaultOrder,
    groupedMeasurementsClone,
  );

  return [groupedMeasurementsOrdered, orderedMeasurementsDefaultOrder];
}

export function preserveMeasurementsOrder(
  groupedMeasurements: GroupedMeasurement[],
): [GroupedMeasurement[], OrderedMeasurement[]] {
  const groupedMeasurementsClone = cloneDeep(groupedMeasurements);
  let nextOrder = getHighestMeasurementOrder(groupedMeasurementsClone);

  groupedMeasurementsClone.forEach((groupedMeasurement) => {
    if (groupedMeasurement.panels?.length > 0) {
      groupedMeasurement.panels.forEach((panel) => {
        if (panel.order == null) {
          nextOrder += 1;
          panel.order = nextOrder;
        }
      });

      return;
    }

    if (isOrderableGroupedMeasurement(groupedMeasurement) && groupedMeasurement.order == null) {
      nextOrder += 1;
      groupedMeasurement.order = nextOrder;
    }
  });

  const orderedMeasurements = rebuildOrderedMeasurementsOrder(
    groupedMeasurementsToOrderedMeasurements(groupedMeasurementsClone.filter(isOrderableGroupedMeasurement)).sort(
      (left, right) => (left.order ?? Number.MAX_SAFE_INTEGER) - (right.order ?? Number.MAX_SAFE_INTEGER),
    ),
  );
  const groupedMeasurementsOrdered = setGroupedMeasurementsOrder(orderedMeasurements, groupedMeasurementsClone);

  return [groupedMeasurementsOrdered, orderedMeasurements];
}

function groupedMeasurementsToOrderedMeasurements(groupedMeasurements: GroupedMeasurement[]): OrderedMeasurement[] {
  return groupedMeasurements.flatMap((groupedMeasurement) => {
    if (groupedMeasurement.panels?.length > 0) {
      return groupedMeasurement.panels.map(
        (panel) =>
          ({
            panelAssetId: panel.panelAssetId,
            panelNo: panel.panelNo,
            panelName: panel.name,
            name: panel.name,
            type: groupedMeasurement.type,
            access_location: groupedMeasurement.access_location,
            component: groupedMeasurement.component,
            subloc: groupedMeasurement.subloc,
            accessory: groupedMeasurement.accessory,
            order: panel.order,
            parentAssetId: groupedMeasurement.parentAssetId,
            parentAssetType: groupedMeasurement.parentAssetType,
            parentAssetName: groupedMeasurement.parentAssetName,
          }) as OrderedMeasurement,
      );
    }

    return [
      {
        ...groupedMeasurement,
        panelAssetId: (groupedMeasurement as Partial<OrderedMeasurement>).panelAssetId,
        panelNo: groupedMeasurement.panelNo,
        panelName: groupedMeasurement.panelName,
        name: groupedMeasurement.name,
      } as OrderedMeasurement,
    ];
  });
}

export function rebuildOrderedMeasurementsOrder(orderedMeasurements: OrderedMeasurement[]): OrderedMeasurement[] {
  let index = 1;
  orderedMeasurements.forEach((measurement) => {
    measurement.order = index;
    index++;
  });

  return orderedMeasurements;
}

export function findGroupedMeasurementByGroupId(
  groupedMeasurements: GroupedMeasurement[],
  groupId: string,
): GroupedMeasurement | undefined {
  return groupedMeasurements.find((groupedMeasurement) => groupedMeasurement.groupId === groupId);
}

export function findDuplicateMeasurements(measurements: GroupedMeasurement[]): string[] {
  const duplicates = measurements.filter((measurement) =>
    measurements.some((candidate) => isDuplicateMeasurement(candidate, measurement)),
  );

  return duplicates.map((measurement) => measurement.groupId);
}

export function setGroupedMeasurementsOrder(
  orderedMeasurements: OrderedMeasurement[],
  groupedMeasurements: GroupedMeasurement[],
): GroupedMeasurement[] {
  orderedMeasurements.forEach((ordered) => {
    ordered.type = ordered.type ?? undefined;
    ordered.accessory = ordered.accessory ?? undefined;
    ordered.access_location = ordered.access_location ?? undefined;
    ordered.component = ordered.component ?? undefined;
    ordered.subloc = ordered.subloc ?? undefined;
  });

  groupedMeasurements.forEach((grouped) => {
    grouped.type = grouped.type ?? undefined;
    grouped.accessory = grouped.accessory ?? undefined;
    grouped.access_location = grouped.access_location ?? undefined;
    grouped.component = grouped.component ?? undefined;
    grouped.subloc = grouped.subloc ?? undefined;
  });

  orderedMeasurements.forEach((ordered) => {
    groupedMeasurements
      .filter((grouped) => areEquivalentMeasures(ordered, grouped))
      .forEach((grouped) => {
        if (grouped.panels?.length > 0) {
          grouped.panels.forEach((panel) => {
            if (panel.panelNo === ordered.panelNo) {
              panel.order = ordered.order;
            }
          });
        } else {
          grouped.order = ordered.order;
        }
      });
  });

  return groupedMeasurements;
}

function setOrderedMeasurementsDefaultOrder(orderedMeasurements: OrderedMeasurement[]): OrderedMeasurement[] {
  return [...orderedMeasurements].sort(compareOrderedMeasurementDefaultOrder).map((measurement, index) => ({
    ...measurement,
    order: index + 1,
  }));
}

function compareOrderedMeasurementDefaultOrder(left: OrderedMeasurement, right: OrderedMeasurement): number {
  const parentAssetComparison = compareParentAssetContext(left, right);

  if (parentAssetComparison !== 0) {
    return parentAssetComparison;
  }

  const typeComparison = compareByConfiguredOrder(left.type, right.type, measurementTypeOrder);
  if (typeComparison !== 0) {
    return typeComparison;
  }

  const accessoryComparison = compareByConfiguredOrder(left.accessory, right.accessory, measurementAccessoryOrder);
  if (accessoryComparison !== 0) {
    return accessoryComparison;
  }

  const accessLocationComparison = compareByConfiguredOrder(
    left.access_location,
    right.access_location,
    accessLocationOrder,
  );
  if (accessLocationComparison !== 0) {
    return accessLocationComparison;
  }

  const panelComparison = compareTwoPanelNumbers(left.panelNo, right.panelNo, "asc");
  if (panelComparison !== 0) {
    return panelComparison;
  }

  const componentComparison = compareByConfiguredOrder(left.component, right.component, measurementComponentOrder);
  if (componentComparison !== 0) {
    return componentComparison;
  }

  const subLocationComparison = compareByConfiguredOrder(left.subloc, right.subloc, subLocationOrder);
  if (subLocationComparison !== 0) {
    return subLocationComparison;
  }

  return (left.order ?? Number.MAX_SAFE_INTEGER) - (right.order ?? Number.MAX_SAFE_INTEGER);
}

function compareParentAssetContext(left: OrderedMeasurement, right: OrderedMeasurement): number {
  const leftId = left.parentAssetId;
  const rightId = right.parentAssetId;

  if (leftId != null && rightId != null) {
    if (leftId === rightId) {
      return 0;
    }

    const leftIdAsNumber = Number(leftId);
    const rightIdAsNumber = Number(rightId);

    if (!Number.isNaN(leftIdAsNumber) && !Number.isNaN(rightIdAsNumber)) {
      return leftIdAsNumber - rightIdAsNumber;
    }

    return String(leftId).localeCompare(String(rightId));
  }

  if (left.parentAssetType !== right.parentAssetType) {
    return (left.parentAssetType || "").localeCompare(right.parentAssetType || "");
  }

  return String(left.parentAssetName ?? "").localeCompare(String(right.parentAssetName ?? ""));
}

function compareByConfiguredOrder<T>(
  left: T | undefined,
  right: T | undefined,
  orderMap: ReadonlyMap<T | undefined, number>,
): number {
  return getConfiguredOrderIndex(left, orderMap) - getConfiguredOrderIndex(right, orderMap);
}

function getConfiguredOrderIndex<T>(value: T | undefined, orderMap: ReadonlyMap<T | undefined, number>): number {
  return orderMap.get(value) ?? Number.MAX_SAFE_INTEGER;
}

function createOrderMap<T>(values: T[]): Map<T, number> {
  return new Map(values.map((value, index) => [value, index]));
}

function getHighestMeasurementOrder(groupedMeasurements: GroupedMeasurement[]): number {
  const allOrders = groupedMeasurements.flatMap((groupedMeasurement) =>
    groupedMeasurement.panels?.length > 0
      ? groupedMeasurement.panels.map((panel) => panel.order)
      : isOrderableGroupedMeasurement(groupedMeasurement)
        ? [groupedMeasurement.order]
        : [],
  );

  return allOrders.reduce((highestOrder, currentOrder) => Math.max(highestOrder, currentOrder ?? 0), 0);
}

function isOrderableGroupedMeasurement(groupedMeasurement: GroupedMeasurement): boolean {
  return Boolean(
    groupedMeasurement.panels?.length > 0 ||
      groupedMeasurement.type ||
      groupedMeasurement.accessory ||
      groupedMeasurement.access_location ||
      groupedMeasurement.component ||
      groupedMeasurement.subloc ||
      groupedMeasurement.parentAssetId ||
      groupedMeasurement.parentAssetType ||
      groupedMeasurement.parentAssetName,
  );
}

function areEquivalentMeasures(ordered: OrderedMeasurement, grouped: GroupedMeasurement): boolean {
  const measurementDetailsMatch =
    ordered.type === grouped.type &&
    ordered.accessory === grouped.accessory &&
    ordered.access_location === grouped.access_location &&
    ordered.component === grouped.component &&
    ordered.subloc === grouped.subloc;

  if (!measurementDetailsMatch) return false;

  if (ordered.parentAssetId != null && grouped.parentAssetId != null)
    return ordered.parentAssetId === grouped.parentAssetId;

  const hasNoParentDetails =
    ordered.parentAssetType == null &&
    grouped.parentAssetType == null &&
    ordered.parentAssetName == null &&
    grouped.parentAssetName == null;

  if (hasNoParentDetails) return true;

  const parentAssetTypeMatches = ordered.parentAssetType === grouped.parentAssetType;
  const parentAssetNameMatches = ordered.parentAssetName === grouped.parentAssetName;

  return parentAssetTypeMatches && parentAssetNameMatches;
}

function isDuplicateMeasurement(left: GroupedMeasurement, right: GroupedMeasurement): boolean {
  return (
    left.type === right.type &&
    left.access_location === right.access_location &&
    left.component === right.component &&
    left.subloc === right.subloc &&
    left.parentAssetName === right.parentAssetName &&
    left.groupId !== right.groupId
  );
}
